import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, FilePdf, DownloadSimple, MagnifyingGlass, Lock } from "@phosphor-icons/react";

export default function EmployeeFilesPage() {
  const { bid } = useParams();
  const navigate = useNavigate();
  const [books, setBooks] = useState(null);
  const [book, setBook] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/employee-books").then((r) => setBooks(r.data.books || [])).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    setBook(null);
    if (bid) api.get(`/employee-books/${bid}`).then((r) => setBook(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [bid]);

  const shown = (books || []).filter((b) => !q || b.employee.toLowerCase().includes(q.toLowerCase()));

  if (bid) {
    return (
      <div className="space-y-6" data-testid="employee-file-page">
        <button onClick={() => navigate("/employee-files")} className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-500 hover:text-stone-900" data-testid="employee-file-back">
          <ArrowLeft size={15} /> All employee files
        </button>
        {!book ? (
          <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
        ) : (
          <>
            <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="font-heading text-3xl font-bold text-stone-900" data-testid="employee-file-name">{book.employee}</h1>
                  <p className="mt-1 text-sm text-stone-500">
                    Employee ID {book.employee_id} · {book.status} · {book.pages} pages · locked auditor book
                  </p>
                  {book.live_user_id && (
                    <Link to={`/team/${book.live_user_id}`} className="mt-2 inline-block text-sm font-medium text-primary hover:underline" data-testid="employee-file-live-link">
                      Open live employee record →
                    </Link>
                  )}
                </div>
                <a href={`${API_BASE}/employee-books/${book.id}/pdf`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-600 transition-colors hover:bg-stone-900 hover:text-white"
                  data-testid="employee-file-download">
                  <DownloadSimple size={16} /> Download PDF
                </a>
              </div>
              {(book.findings || []).length > 0 && (
                <div className="mt-5 border-t border-stone-100 pt-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-400">Current findings</p>
                  <ul className="space-y-2" data-testid="employee-file-findings">
                    {book.findings.map((f, i) => (
                      <li key={i} className="rounded-lg bg-accent/50 px-3 py-2 text-sm leading-relaxed text-stone-600">{f}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
              <div className="flex items-center gap-2 border-b border-stone-100 px-4 py-2.5">
                <Lock size={13} className="text-stone-400" />
                <p className="text-xs font-medium text-stone-500">Locked auditor book — original document, read-only</p>
              </div>
              <iframe title="Auditor book" src={`${API_BASE}/employee-books/${book.id}/pdf`} className="h-[75vh] w-full" data-testid="employee-file-pdf" />
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6" data-testid="employee-files-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-stone-900" data-testid="employee-files-title">Employee files</h1>
        <p className="mt-1 text-sm text-stone-500">
          Locked auditor books — the complete historical file for each employee, exactly as prepared for audit.
          Admins see everyone; employees see only their own file.
        </p>
      </div>
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…"
          className="h-11 w-full rounded-full border border-stone-300 bg-white pl-10 pr-4 text-sm focus:border-primary focus:outline-none" data-testid="employee-files-search" />
      </div>
      {books === null ? (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : shown.length === 0 ? (
        <p className="rounded-xl border border-dashed border-stone-300 bg-white p-12 text-center text-sm text-stone-400" data-testid="employee-files-empty">
          {q ? "No files match that name." : "No employee files loaded yet."}
        </p>
      ) : (
        <div className="space-y-3">
          {shown.map((b) => (
            <Link key={b.id} to={`/employee-files/${b.id}`}
              className="flex items-center gap-4 rounded-xl border border-stone-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              data-testid={`employee-file-row-${b.employee_id}`}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><FilePdf size={20} /></span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-heading text-base font-semibold text-stone-900">{b.employee}</span>
                <span className="block text-xs text-stone-500">ID {b.employee_id} · {b.status} · {b.pages} pages</span>
              </span>
              {b.live_user_id
                ? <span className="rounded-full bg-[#27AE60]/10 px-2.5 py-1 text-[10px] font-semibold text-[#1e8449]">live record linked</span>
                : <span className="rounded-full bg-stone-100 px-2.5 py-1 text-[10px] font-semibold text-stone-400">historical file</span>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
